<script>
import { ref } from 'vue';
import AccordionComponent from '../components/Accordion.vue';

export default {
  name: 'ImmigrationServices',
  components: {
    AccordionComponent
  },
  setup() {
    // Reactive reference for the active tab
    const activeTab = ref('visit');

    // Data for Visit Visas
    const visitVisaCountries = ref([
      { name: 'All countries visit', description: 'Dreaming of exploring the world? Our expert guidance simplifies the complex visa application process for any destination, ensuring a smooth journey from application to approval. Travel with confidence, knowing all details are meticulously handled.' },
      { name: 'South Korea', description: 'Immerse yourself in the vibrant culture and breathtaking landscapes of South Korea. From the bustling streets of Seoul to serene temples, we make your visit seamless and unforgettable. Experience the magic of the Land of the Morning Calm with ease.' },
      { name: 'Japan', description: 'Discover the harmonious blend of ancient traditions and futuristic innovations in Japan. Whether it\'s the cherry blossoms of Kyoto or the neon glow of Tokyo, our service ensures your journey to this captivating nation is effortless and enriching.' },
      { name: 'UK', description: 'Experience the rich history and diverse culture of the United Kingdom. From the iconic landmarks of London to the scenic beauty of the Scottish Highlands, we provide comprehensive support for your visit, making your British adventure a reality.' },
      { name: 'USA', description: 'Explore the vast opportunities and diverse landscapes of the United States. From vibrant cities to stunning national parks, our expert assistance ensures your visit visa application is handled with precision, opening doors to your American dream.' },
      { name: 'Canada', description: 'Embark on an adventure to Canada, a land of majestic natural beauty and welcoming communities. Whether for leisure or family visits, our dedicated support makes your Canadian journey smooth, allowing you to create lasting memories.' },
      { name: 'Malta', description: 'Unwind in the sun-drenched beauty and historical richness of Malta. This Mediterranean gem offers a unique blend of cultures and stunning coastlines. We simplify your visa process, so you can enjoy Malta\'s charm without a worry.' },
      { name: 'Australians', description: 'Discover the unique wildlife and stunning natural wonders of Australia. From the outback to vibrant coastal cities, our streamlined visa services help you explore the Land Down Under with confidence and ease.' },
      { name: 'Kuwait', description: 'Experience the modern marvels and rich heritage of Kuwait. Our tailored visa solutions ensure a smooth entry into this dynamic Middle Eastern hub, whether for tourism or family visits. Discover Kuwait with peace of mind.' },
      { name: 'Kenya', description: 'Journey to Kenya, the heart of African safaris and breathtaking landscapes. Witness incredible wildlife and vibrant cultures. Our visa experts make your trip to this magnificent country effortless, allowing you to focus on adventure.' },
      { name: 'Southafrica', description: 'Explore the diverse beauty and vibrant spirit of South Africa. From Table Mountain to Kruger National Park, our comprehensive visa support ensures your journey to this captivating nation is smooth and memorable.' },
      { name: 'France', description: 'Indulge in the romance and elegance of France. From Parisian boulevards to the vineyards of Bordeaux, our visa services ensure your French escapade is as enchanting as you imagine, handling all the details with care.' },
      { name: 'Germany', description: 'Discover the rich history, innovative spirit, and picturesque landscapes of Germany. Whether for cultural exploration or visiting loved ones, our expert guidance makes your German visit visa application straightforward and successful.' }
    ]);

    // Data for Study Visas
    const studyVisaCountries = ref([
      { name: 'All of Europe', description: 'Unlock unparalleled educational opportunities across Europe. From historic universities to cutting-edge research institutions, we pave your way to a world-class education, simplifying every step of your study visa application.' },
      { name: 'UK', description: 'Pursue academic excellence in the United Kingdom. With a legacy of prestigious universities and diverse courses, we guide you through the UK study visa process, ensuring your path to global knowledge is clear and achievable.' },
      { name: 'France', description: 'Immerse yourself in French culture while pursuing your studies. France offers renowned institutions and a vibrant student life. Our dedicated support makes your French study visa application smooth, opening doors to a rich academic experience.' },
      { name: 'Canada', description: 'Achieve your academic aspirations in Canada, a country known for its high-quality education and welcoming environment. We streamline your study permit application, helping you secure a future of learning and growth in a diverse nation.' },
      { name: 'USA', description: 'Gain a competitive edge with an education in the United States. Home to leading universities and innovative programs, we expertly navigate the complexities of the US student visa, empowering your journey towards academic and professional success.' }
    ]);

    // Data for Work Visas
    const workVisaCountries = ref([
      { name: 'Kuwait', description: 'Advance your career in the thriving economy of Kuwait. We provide comprehensive support for your work visa application, transforming complex regulations into a clear pathway for professional growth and a prosperous future in the Middle East.' },
      { name: 'Canada', description: 'Unlock global career opportunities in Canada. With a strong job market and a welcoming immigration policy, we guide you through the work permit process, helping you secure your professional future and build a new life in a vibrant, multicultural nation.' }
    ]);

    // Data for Consultation Services
    const consultationServices = ref([
      { name: 'Consultation guide', description: 'Gain clarity and confidence with our expert consultation guides. We address your specific immigration concerns, providing personalized advice and strategic insights to empower your journey. Make informed decisions every step of the way.' },
      { name: 'Counseling', description: 'Receive personalized counseling to navigate your immigration journey. Our experienced advisors offer empathetic support and clear guidance, helping you overcome challenges and achieve your aspirations with peace of mind.' },
      { name: 'Documents vetting', description: 'Ensure your application is flawless with our meticulous documents vetting service. We meticulously review every detail, identifying and rectifying potential issues to maximize your chances of approval and minimize delays.' },
      { name: 'Pof service', description: 'Strengthen your visa application with our Proof of Funds (POF) service. We guide you in demonstrating financial stability, ensuring all requirements are met to build a robust case for your immigration success.' },
      { name: 'Sop', description: 'Craft a compelling Statement of Purpose (SOP) that highlights your unique aspirations and qualifications. Our experts help you articulate your story effectively, making your application stand out to immigration authorities.' },
      { name: 'Advisory', description: 'Benefit from strategic advisory services tailored to your immigration goals. We provide up-to-date information and expert recommendations, empowering you to make the best choices for your future abroad.' },
      { name: 'Flight Itinerary', description: 'Secure your travel plans with our reliable flight itinerary service. We provide confirmed flight details essential for your visa application, ensuring a smooth and stress-free process from start to finish.' },
      { name: 'Vacation', description: 'Plan your perfect getaway with our vacation visa assistance. We handle all the complexities, allowing you to relax and look forward to an unforgettable travel experience, whether for leisure, tourism, or visiting loved ones.' }
    ]);

    return {
      activeTab,
      visitVisaCountries,
      studyVisaCountries,
      workVisaCountries,
      consultationServices
    }
  }
}
</script>

<template>
  <div class="container mx-auto py-8">

    <!-- Navigation Tabs -->
    <div class="mb-4">
      <button
        class="inline-block py-2 px-4 rounded-t-lg font-semibold"
        :class="{ 'bg-white border-l border-t border-r rounded-t text-primary': activeTab === 'visit', 'hover:text-primary': activeTab !== 'visit' }"
        @click="activeTab = 'visit'"
      >
        Visit Visas
      </button>
      <button
        class="inline-block py-2 px-4 rounded-t-lg font-semibold"
        :class="{ 'bg-white border-l border-t border-r rounded-t text-primary': activeTab === 'study', 'hover:text-primary': activeTab !== 'study' }"
        @click="activeTab = 'study'"
      >
        Study Visas
      </button>
      <button
        class="inline-block py-2 px-4 rounded-t-lg font-semibold"
        :class="{ 'bg-white border-l border-t border-r rounded-t text-primary': activeTab === 'work', 'hover:text-primary': activeTab !== 'work' }"
        @click="activeTab = 'work'"
      >
        Work Visas
      </button>
      <button
        class="inline-block py-2 px-4 rounded-t-lg font-semibold"
        :class="{ 'bg-white border-l border-t border-r rounded-t text-primary': activeTab === 'consultation', 'hover:text-primary': activeTab !== 'consultation' }"
        @click="activeTab = 'consultation'"
      >
        Consultation Guides
      </button>
    </div>

    <transition name="tab" mode="out-in">
      <div :key="activeTab">
    <!-- Visit Visas -->
    <section v-if="activeTab === 'visit'" class="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
      <AccordionComponent v-for="country in visitVisaCountries" :key="country.name" :title="country.name">
        {{ country.description }}
      </AccordionComponent>
    </section>

    <!-- Study Visas -->
    <section v-if="activeTab === 'study'" class="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
      <AccordionComponent v-for="country in studyVisaCountries" :key="country.name" :title="country.name">
        {{ country.description }}
      </AccordionComponent>
    </section>

    <!-- Work Visas -->
    <section v-if="activeTab === 'work'" class="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
      <AccordionComponent v-for="country in workVisaCountries" :key="country.name" :title="country.name">
        {{ country.description }}
      </AccordionComponent>
    </section>

    <!-- Consultation Services -->
    <section v-if="activeTab === 'consultation'" class="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
      <AccordionComponent v-for="service in consultationServices" :key="service.name" :title="service.name">
        {{ service.description }}
      </AccordionComponent>
    </section>
      </div>
    </transition>

    <!-- Ready to Start Your Journey Section -->
    <section class="text-center py-12" v-reveal:up>
      <h2 class="text-3xl font-semibold mb-4">Ready to Start Your Journey?</h2>
      <p class="text-gray-700 mb-8">Let our experts guide you through the process.</p>
      <button
        @click="$router.push({ name: 'scheduling' })"
        class="bg-primary text-white font-semibold py-2 px-6 rounded-lg hover:bg-primary/90 hover:scale-105 transition duration-300"
      >
        Schedule a Consultation
      </button>
    </section>
  </div>
</template>

<style scoped>
.container {
  font-family: 'Inter', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Navigation Tabs Styling */
.inline-block {
  border-bottom: 2px solid transparent;
  transition: border-bottom-color 0.3s ease, color 0.3s ease;
}

.inline-block.bg-white {
  border-color: #D62828; /* Active tab indicator */
  color: #D62828; /* Active tab text color */
}

.inline-block:hover:not(.bg-white) {
  border-bottom-color: #f0f0f0; /* Light border on hover for inactive tabs */
  color: #D62828; /* Primary color on hover for inactive tabs */
}

/* Accordion Styling within sections */
section.mb-8 {
  background-color: #ffffff; /* White background for sections */
  padding: 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

/* Ready to Start Your Journey Section */
section.text-center.py-12 {
  background-color: #f0f4f8; /* Light blue-gray background */
  border-radius: 0.75rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 3rem 0;
}

section.text-center.py-12 h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1rem;
}

section.text-center.py-12 p {
  font-size: 1.25rem;
  color: #555;
  margin-bottom: 2rem;
}

section.text-center.py-12 button {
  background-color: #D62828;
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  transition: background-color 0.3s ease;
}

section.text-center.py-12 button:hover {
  background-color: #b02020;
}

.tab-enter-active,
.tab-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.tab-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.tab-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (prefers-reduced-motion: reduce) {
  .tab-enter-active,
  .tab-leave-active {
    transition: none;
  }
}
</style>